import { apiClient } from '@/api/client'
import { endpoints } from '@/api/endpoints'

type Option = { id: number; name: string }

export type ScPurchaseOrderListRow = {
  id: number
  code: string
  reference: string
  requisitionCode: string
  vendorId: number
  vendorName: string
  orderDate: string
  estimatedDeliveryDate: string
  total: number
}

export type ScPurchaseOrderMaster = {
  id: number
  code: string
  requisitionId: number
  requisitionCode: string
  vendorId: number
  vendorName: string
  date: string
  branchId: number
  reference: string
  estimatedDeliveryDate: string
  paymentTerms: number
  paymentTermsName: string
  attachmentPath: string
  taxableAmount: number
  taxAmount: number
  discount: number
  total: number
  status: number
  createdDate?: string
  createdBy?: number
  updatedDate?: string
  updatedById?: number
}

export type ScPurchaseOrderLine = {
  id: number
  orderId: number
  itemId: number
  itemName: string
  description: string
  quantity: number
  unitPrice: number
  discountPercent: number
  discount: number
  discountAmount: number
  taxableAmount: number
  tax: number
  taxPercent: number
  taxAmount: number
  lineAmount: number
  isDeleted: string
}

export type ScRequisitionOption = { id: number; code: string }

export type ScTaxOption = { id: number; name: string; percent: number }

export type ScPurchaseOrderSavePayload = ScPurchaseOrderMaster & {
  attachmentFile?: File | null
  lines: ScPurchaseOrderLine[]
}

function unwrap(payload: any) {
  if (payload && typeof payload === 'object') {
    if ('success' in payload && 'data' in payload) return payload.data
    if ('data' in payload) return payload.data
  }
  return payload
}

function toArray(payload: any): any[] {
  const raw = unwrap(payload)
  if (Array.isArray(raw)) return raw
  if (raw && typeof raw === 'object' && Array.isArray((raw as any).data)) return (raw as any).data
  if (raw && typeof raw === 'object' && Array.isArray((raw as any).items)) return (raw as any).items
  if (raw && typeof raw === 'object' && Array.isArray((raw as any).result)) return (raw as any).result
  return []
}

function num(v: unknown, fallback = 0) {
  const n = Number(v)
  return Number.isFinite(n) ? n : fallback
}

function str(v: unknown) {
  return typeof v === 'string' ? v : String(v ?? '')
}

function dateOnly(v: unknown): string {
  const s = str(v)
  return s.includes('T') ? s.split('T')[0] : s
}

function append(fd: FormData, key: string, value: unknown) {
  if (value === undefined || value === null) {
    fd.append(key, '')
    return
  }
  fd.append(key, String(value))
}

export class ScPurchaseOrderService {
  private itemNameCache = new Map<number, string>()
  private vendorNameCache = new Map<number, string>()

  private async getItemNameById(itemId: number, branchId: number): Promise<string> {
    const id = num(itemId)
    if (!branchId || id <= 0) return ''
    const cached = this.itemNameCache.get(id)
    if (cached) return cached
    const response = await apiClient.get(endpoints.items.getById(id, branchId))
    const raw = unwrap(response.data)
    const source = Array.isArray(raw) ? raw[0] : Array.isArray(raw?.data) ? raw.data[0] : raw
    const name = str(
      source?.itemNameEn ??
        source?.ItemNameEn ??
        source?.itemNameAr ??
        source?.ItemNameAr ??
        source?.itemName ??
        source?.ItemName ??
        source?.name ??
        source?.Name,
    ).trim()
    if (name) this.itemNameCache.set(id, name)
    return name
  }

  private async getVendorNameById(vendorId: number, branchId: number): Promise<string> {
    const id = num(vendorId)
    if (!branchId || id <= 0) return ''
    const cached = this.vendorNameCache.get(id)
    if (cached) return cached
    const response = await apiClient.get(endpoints.vendors.getById(id, branchId))
    const raw = unwrap(response.data)
    const source = Array.isArray(raw) ? raw[0] : Array.isArray(raw?.data) ? raw.data[0] : raw
    const name = str(
      source?.vendorNameEn ??
        source?.VendorNameEn ??
        source?.vendorNameAr ??
        source?.VendorNameAr ??
        source?.vendorName ??
        source?.VendorName ??
        source?.name ??
        source?.Name,
    ).trim()
    if (name) this.vendorNameCache.set(id, name)
    return name
  }

  async getAll(
    branchId: number,
    pageNumber: number,
    pageSize: number,
  ): Promise<{ items: ScPurchaseOrderListRow[]; totalCount: number }> {
    const response = await apiClient.get(endpoints.scPurchaseOrder.getAll(branchId, pageNumber, pageSize), {
      // list pages often want empty state, not global toasts
      skipHandledLocallyErrorUI: true,
    } as any)
    const payload = response.data
    const top = payload && typeof payload === 'object' && !Array.isArray(payload) ? (payload as any) : null
    const raw = unwrap(payload)
    const arr = toArray(raw)
    const items = arr.map((x: any) => ({
      id: num(x?.id ?? x?.Id),
      code: str(x?.code ?? x?.Code),
      reference: str(x?.reference ?? x?.Reference),
      requisitionCode: str(x?.requisitionCode ?? x?.RequisitionCode ?? x?.requisition ?? ''),
      vendorId: num(x?.vendorId ?? x?.VendorId),
      vendorName: str(x?.vendorName ?? x?.VendorName ?? ''),
      orderDate: dateOnly(x?.date ?? x?.Date ?? x?.orderDate ?? x?.OrderDate),
      estimatedDeliveryDate: dateOnly(x?.estimatedDeliveryDate ?? x?.EstimatedDeliveryDate),
      total: num(x?.total ?? x?.Total),
    }))

    // If vendorName is missing, resolve by vendorId (cached).
    const missingVendorIds = Array.from(
      new Set(items.filter((r) => !r.vendorName.trim() && r.vendorId > 0).map((r) => r.vendorId)),
    )
    if (missingVendorIds.length > 0) {
      const resolvedPairs = await Promise.all(
        missingVendorIds.map(async (vendorId) => {
          const name = await this.getVendorNameById(vendorId, branchId)
          return { vendorId, name }
        }),
      )
      const nameById = new Map<number, string>(resolvedPairs.map((p) => [p.vendorId, p.name]))
      for (const r of items) {
        if (!r.vendorName.trim() && r.vendorId > 0) {
          r.vendorName = nameById.get(r.vendorId) || ''
        }
      }
    }

    const totalCount = num(
      (raw && typeof raw === 'object' && !Array.isArray(raw) ? (raw as any).totalRecords ?? (raw as any).totalCount : undefined) ??
        top?.totalRecords ??
        top?.totalCount ??
        items.length,
      items.length,
    )
    return { items, totalCount }
  }

  async getById(id: number, branchId: number): Promise<ScPurchaseOrderMaster> {
    const response = await apiClient.get(endpoints.scPurchaseOrder.getById(id, branchId))
    const raw = unwrap(response.data)
    const source = Array.isArray(raw) ? raw[0] : Array.isArray(raw?.data) ? raw.data[0] : raw
    return {
      id: num(source?.id ?? source?.Id),
      code: str(source?.code ?? source?.Code),
      requisitionId: num(source?.requisitionId ?? source?.RequisitionId),
      requisitionCode: str(source?.requisitionCode ?? source?.RequisitionCode),
      vendorId: num(source?.vendorId ?? source?.VendorId),
      vendorName: str(source?.vendorName ?? source?.VendorName),
      date: dateOnly(source?.date ?? source?.Date),
      branchId: num(source?.branchId ?? source?.BranchId),
      reference: str(source?.reference ?? source?.Reference),
      estimatedDeliveryDate: dateOnly(source?.estimatedDeliveryDate ?? source?.EstimatedDeliveryDate),
      paymentTerms: num(source?.paymentTerms ?? source?.PaymentTerms),
      paymentTermsName: str(source?.paymentTermsName ?? source?.PaymentTermsName),
      attachmentPath: str(source?.attachmentPath ?? source?.AttachmentPath),
      taxableAmount: num(source?.taxableAmount ?? source?.TaxableAmount),
      taxAmount: num(source?.taxAmount ?? source?.TaxAmount),
      discount: num(source?.discount ?? source?.Discount),
      total: num(source?.total ?? source?.Total),
      status: num(source?.status ?? source?.Status),
      createdDate: source?.createdDate ?? source?.CreatedDate,
      createdBy: source?.createdBy ?? source?.CreatedBy,
      updatedDate: source?.updatedDate ?? source?.UpdatedDate,
      updatedById: source?.updatedById ?? source?.UpdatedById,
    }
  }

  async getLinesByOrderId(orderId: number, branchId: number): Promise<ScPurchaseOrderLine[]> {
    const response = await apiClient.get(endpoints.scPurchaseOrder.getLinesByOrderId(orderId, branchId))
    const arr = toArray(response.data)
    return arr.map((x: any) => ({
      id: num(x?.id ?? x?.Id),
      orderId: num(x?.orderId ?? x?.OrderId),
      itemId: num(x?.itemId ?? x?.ItemId),
      itemName: str(x?.itemName ?? x?.ItemName),
      description: str(x?.description ?? x?.Description),
      quantity: num(x?.quantity ?? x?.Quantity),
      unitPrice: num(x?.unitPrice ?? x?.UnitPrice),
      discountPercent: num(x?.discountPercent ?? x?.DiscountPercent),
      discount: num(x?.discount ?? x?.Discount),
      discountAmount: num(x?.discountAmount ?? x?.DiscountAmount),
      taxableAmount: num(x?.taxableAmount ?? x?.TaxableAmount),
      tax: num(x?.tax ?? x?.Tax),
      taxPercent: num(x?.taxPercent ?? x?.TaxPercent),
      taxAmount: num(x?.taxAmount ?? x?.TaxAmount),
      lineAmount: num(x?.lineAmount ?? x?.LineAmount),
      isDeleted: str(x?.isDeleted ?? x?.IsDeleted ?? 'false'),
    }))
  }

  async getRequisitions(branchId: number): Promise<ScRequisitionOption[]> {
    const response = await apiClient.get(endpoints.scPurchaseOrder.getRequisitions(branchId))
    const arr = toArray(response.data)
    return arr
      .map((x: any) => ({
        id: num(x?.id ?? x?.requisitionId ?? x?.RequisitionId ?? x?.Id),
        code: str(x?.code ?? x?.requisitionCode ?? x?.RequisitionCode ?? x?.Code),
      }))
      .filter((x) => x.id > 0 && x.code.trim())
  }

  /** Returns requisition lines used to auto-fill PO lines. */
  async getRequisitionById(requisitionId: number, branchId: number): Promise<Array<{ itemId: number; itemName: string; quantity: number }>> {
    const response = await apiClient.get(endpoints.scPurchaseOrder.getRequisitionById(requisitionId, branchId))
    const raw = unwrap(response.data)
    const master = Array.isArray(raw) ? raw[0] : raw
    const lines = toArray(master?.lines ?? master?.Lines ?? master)
    const mapped = lines.map((x: any) => ({
      itemId: num(x?.itemId ?? x?.ItemId),
      itemName: str(x?.itemName ?? x?.ItemName ?? x?.itemNameEn ?? x?.ItemNameEn ?? x?.itemNameAr ?? x?.ItemNameAr),
      quantity: num(x?.quantity ?? x?.Quantity),
    }))
    const filtered = mapped.filter((l) => l.itemId > 0 && l.quantity > 0)

    // If requisition API doesn't include itemName, resolve via Items GetById.
    const missing = Array.from(new Set(filtered.filter((l) => !String(l.itemName || '').trim()).map((l) => l.itemId)))
    if (missing.length === 0) return filtered

    const resolvedPairs = await Promise.all(
      missing.map(async (itemId) => {
        const name = await this.getItemNameById(itemId, branchId)
        return { itemId, name }
      }),
    )
    const nameById = new Map<number, string>(resolvedPairs.map((p) => [p.itemId, p.name]))
    return filtered.map((l) => ({
      ...l,
      itemName: String(l.itemName || '').trim() ? l.itemName : nameById.get(l.itemId) || '',
    }))
  }

  async getVendors(branchId: number): Promise<Option[]> {
    const response = await apiClient.get(endpoints.scPurchaseOrder.getVendors(branchId))
    const arr = toArray(response.data)
    return arr
      .map((x: any) => ({
        id: num(x?.vendorId ?? x?.VendorId ?? x?.id ?? x?.Id),
        name: str(x?.vendorNameEn ?? x?.VendorNameEn ?? x?.vendorNameAr ?? x?.VendorNameAr ?? x?.vendorName ?? x?.VendorName ?? x?.name ?? x?.Name),
      }))
      .filter((x) => x.id > 0)
  }

  async getTaxes(branchId: number): Promise<ScTaxOption[]> {
    const response = await apiClient.get(endpoints.scPurchaseOrder.getTaxes(branchId))
    const arr = toArray(response.data)
    return arr
      .map((x: any) => ({
        id: num(x?.taxRateId ?? x?.TaxRateId ?? x?.id ?? x?.Id),
        name: str(x?.taxRateNameEn ?? x?.TaxRateNameEn ?? x?.taxRateNameAr ?? x?.TaxRateNameAr ?? x?.lookupNameEn ?? x?.lookupNameAr ?? x?.name ?? 'Tax'),
        percent: num(x?.taxPercent ?? x?.TaxPercent ?? x?.taxRate ?? x?.TaxRate),
      }))
      .filter((x) => x.id > 0)
  }

  /** Payment terms lookup (reused from Inventory Purchase Orders). */
  async getPaymentTerms(): Promise<Option[]> {
    const response = await apiClient.get(endpoints.purchaseOrders.getPaymentTerms)
    const arr = toArray(response.data)
    return arr
      .map((x: any) => ({
        id: num(x?.id ?? x?.lookupId ?? x?.paymentTerms ?? x?.PaymentTerms),
        name: str(
          x?.lookupNameEn ??
            x?.lookupNameAr ??
            x?.paymentTermsNameEn ??
            x?.paymentTermsNameAr ??
            x?.paymentTermsName ??
            x?.PaymentTermsName ??
            x?.name ??
            x?.Name,
        ),
      }))
      .filter((x) => x.id > 0)
  }

  private normalizeLineForJson(l: ScPurchaseOrderLine): Record<string, number | string> {
    // Follow Swagger sample field order (stable JSON helps some backends)
    return {
      discount: num(l.discount),
      taxAmount: num(l.taxAmount),
      discountPercent: num(l.discountPercent),
      unitPrice: num(l.unitPrice),
      orderId: num(l.orderId),
      quantity: num(l.quantity),
      tax: num(l.tax),
      lineAmount: num(l.lineAmount),
      itemId: num(l.itemId),
      isDeleted: str(l.isDeleted ?? 'false'),
      discountAmount: num(l.discountAmount),
      description: str(l.description ?? ''),
      taxableAmount: num(l.taxableAmount),
      itemName: str(l.itemName ?? ''),
      id: num(l.id),
      taxPercent: num(l.taxPercent),
    }
  }

  private buildOrderLinesJson(lines: ScPurchaseOrderLine[]): string {
    return JSON.stringify(lines.map((l) => this.normalizeLineForJson(l)))
  }

  private buildFormData(payload: ScPurchaseOrderSavePayload, orderLinesJson: string): FormData {
    const fd = new FormData()
    append(fd, 'Id', payload.id)
    append(fd, 'Code', payload.code)
    append(fd, 'RequisitionId', payload.requisitionId)
    append(fd, 'RequisitionCode', payload.requisitionCode)
    append(fd, 'VendorId', payload.vendorId)
    append(fd, 'VendorName', payload.vendorName)
    append(fd, 'Date', payload.date)
    append(fd, 'BranchId', payload.branchId)
    append(fd, 'Reference', payload.reference)
    append(fd, 'EstimatedDeliveryDate', payload.estimatedDeliveryDate)
    append(fd, 'PaymentTerms', payload.paymentTerms)
    append(fd, 'PaymentTermsName', payload.paymentTermsName)
    append(fd, 'AttachmentPath', payload.attachmentPath)
    append(fd, 'TaxableAmount', payload.taxableAmount)
    append(fd, 'TaxAmount', payload.taxAmount)
    append(fd, 'Discount', payload.discount)
    append(fd, 'Total', payload.total)
    append(fd, 'Status', payload.status)
    append(fd, 'CreatedDate', payload.createdDate ?? new Date().toISOString())
    append(fd, 'CreatedBy', payload.createdBy ?? 0)
    append(fd, 'UpdatedDate', payload.updatedDate ?? new Date().toISOString())
    append(fd, 'UpdatedById', payload.updatedById ?? 0)

    // Swagger shows both `Lines` and `orderLinesJson`; we send JSON field to match backend [FromForm] usage.
    append(fd, 'orderLinesJson', orderLinesJson)

    if (payload.attachmentFile) {
      fd.append('AttachmentFile', payload.attachmentFile)
    } else {
      append(fd, 'AttachmentFile', '')
    }
    return fd
  }

  async save(payload: ScPurchaseOrderSavePayload): Promise<void> {
    const orderLinesJson = this.buildOrderLinesJson(payload.lines)
    const fd = this.buildFormData(payload, orderLinesJson)
    await apiClient.post(endpoints.scPurchaseOrder.save, fd, { headers: { 'Content-Type': 'multipart/form-data' } })
  }

  async update(payload: ScPurchaseOrderSavePayload): Promise<void> {
    const orderLinesJson = this.buildOrderLinesJson(payload.lines)
    const fd = this.buildFormData(payload, orderLinesJson)
    await apiClient.put(endpoints.scPurchaseOrder.update, fd)
  }

  async delete(id: number, branchId: number): Promise<void> {
    await apiClient.delete(endpoints.scPurchaseOrder.delete(id, branchId))
  }
}

export const scPurchaseOrderService = new ScPurchaseOrderService()

